import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { dealsApi, customerContactsApi } from '../../../services/api';
import { DealListItem, DealDetail, DealStage, DealProduct } from '../../../types';
import { CustomerContactListItem } from '../../../types/customer-contact';
import { showSuccess } from '../../../utils/toast';
import { DEAL_STAGES, calculateProducts } from '../utils/dealStages';

export const useDealPanel = (load: () => Promise<void>) => {
  const navigate = useNavigate();
  const [selectedDeal, setSelectedDeal] = useState<DealDetail | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDeal, setLoadingDeal] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [customerContacts, setCustomerContacts] = useState<CustomerContactListItem[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const loadCustomerContacts = async (customerId: number | null) => {
    if (!customerId) {
      setCustomerContacts([]);
      return;
    }
    try {
      setLoadingContacts(true);
      const response = await customerContactsApi.getAll({ customer_id: customerId });
      setCustomerContacts(response.data || []);
    } catch (e: any) {
      console.error('Failed to load customer contacts:', e);
      setCustomerContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleViewDetails = async (deal: DealListItem) => {
    try {
      setLoadingDeal(true);
      setPanelError(null);
      const dealDetail = await dealsApi.getById(deal.id) as DealDetail;
      // Debug logging to understand the data structure
      console.log('Deal Detail loaded:', {
        id: dealDetail.id,
        project_request_id: dealDetail.project_request_id,
        customer_id: dealDetail.customer_id,
        hasProjectRequest: !!dealDetail.projectRequest,
        hasProject_request: !!(dealDetail as any).project_request,
        hasCustomer: !!dealDetail.customer,
        projectRequest: dealDetail.projectRequest,
        project_request: (dealDetail as any).project_request,
        customer: dealDetail.customer,
      });
      setSelectedDeal(dealDetail);
      setViewMode('view');
      if (dealDetail.customer_id) {
        await loadCustomerContacts(dealDetail.customer_id);
      }
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Failed to load deal details');
    } finally {
      setLoadingDeal(false);
    }
  };

  const handleEdit = async (deal: DealListItem) => {
    try {
      setLoadingDeal(true);
      setPanelError(null);
      const dealDetail = await dealsApi.getById(deal.id) as DealDetail;
      setSelectedDeal(dealDetail);
      setViewMode('edit');
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Failed to load deal details');
    } finally {
      setLoadingDeal(false);
    }
  };

  const handleClosePanel = () => {
    setSelectedDeal(null);
    setViewMode(null);
    setPanelError(null);
    setCustomerContacts([]);
  };

  const handleDealChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (!selectedDeal) return;
    const { name, value } = e.target;
    setSelectedDeal((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleStageTransition = async (newStage: DealStage) => {
    if (!selectedDeal || submitting) return;
    setSubmitting(true);
    setPanelError(null);
    try {
      const oldStage = selectedDeal.stage;
      const valuePropositionIndex = DEAL_STAGES.indexOf('Value Proposition');
      const oldStageIndex = DEAL_STAGES.indexOf(oldStage);
      const newStageIndex = DEAL_STAGES.indexOf(newStage);

      let productsToSend: DealProduct[] | undefined = undefined;

      // If moving to Value Proposition, calculate products using JS logic
      if (newStage === 'Value Proposition') {
        // Create a temporary deal detail object for calculation
        const tempDeal: DealDetail = {
          ...selectedDeal,
          stage: newStage,
        };
        productsToSend = calculateProducts(tempDeal);
      }
      // If moving away from Value Proposition to earlier stage, send empty array to delete products
      else if (newStageIndex < valuePropositionIndex && oldStageIndex >= valuePropositionIndex) {
        productsToSend = [];
      }

      // Prepare update payload
      const updatePayload: any = {
        stage: newStage,
      };

      // Include products if we calculated them or need to delete them
      if (productsToSend !== undefined) {
        updatePayload.products = productsToSend;
      }

      await dealsApi.update(selectedDeal.id, updatePayload);

      // Reload deal to get updated data including products
      const updatedDeal = await dealsApi.getById(selectedDeal.id) as DealDetail;
      setSelectedDeal(updatedDeal);

      // Reload deals list
      await load();
      showSuccess('Stage updated successfully');
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Failed to update stage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeal || submitting) return;
    setSubmitting(true);
    setPanelError(null);
    try {
      await dealsApi.update(selectedDeal.id, {
        customer_id: selectedDeal.customer_id,
        name: selectedDeal.name,
        type: selectedDeal.type,
        stage: selectedDeal.stage,
        notes: selectedDeal.notes,
      });
      await load();
      handleClosePanel();
      alert('Deal updated successfully');
    } catch (e: any) {
      setPanelError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    selectedDeal,
    setSelectedDeal,
    viewMode,
    setViewMode,
    submitting,
    loadingDeal,
    panelError,
    setPanelError,
    customerContacts,
    loadingContacts,
    loadCustomerContacts,
    handleViewDetails,
    handleEdit,
    handleClosePanel,
    handleDealChange,
    handleStageTransition,
    handleUpdateDeal,
    navigate,
  };
};
